import { getPermissions } from '@/api/permissions'

const state = {
  // 路由权限
  routes: [],
  // 按钮权限
  buttons: {},
  // 权限加载状态
  isLoaded: false
}

const mutations = {
  // 设置路由权限
  SET_ROUTES(state, routes) {
    state.routes = routes
  },
  
  // 设置按钮权限
  SET_BUTTONS(state, buttons) {
    state.buttons = buttons
  },
  
  // 设置权限加载状态
  SET_PERMISSION_LOADED(state, isLoaded) {
    state.isLoaded = isLoaded
  }
}

const actions = {
  /**
   * 获取用户权限并生成动态路由
   */
  async generateRoutes({ commit }) {
    try {
      // 调用API获取权限数据，传递getAll:true参数以获取全部权限
      const response = await getPermissions({ getAll: true })
      
      if (response.code === 'SUCCESS' && response.data) {
        const { menus, buttons } = response.data
        
        // 生成动态路由
        const dynamicRoutes = formatRoutes(menus)
        
        // 设置路由权限
        commit('SET_ROUTES', dynamicRoutes)
        
        // 设置按钮权限
        commit('SET_BUTTONS', buttons || {})
        
        // 标记权限加载完成
        commit('SET_PERMISSION_LOADED', true)
        
        return Promise.resolve(dynamicRoutes)
      } else {
        // 获取权限失败
        commit('SET_PERMISSION_LOADED', false)
        return Promise.reject(new Error('获取权限失败'))
      }
    } catch (error) {
      commit('SET_PERMISSION_LOADED', false)
      return Promise.reject(error)
    }
  },
  
  /**
   * 清除权限数据
   */
  clearPermissions({ commit }) {
    commit('SET_ROUTES', [])
    commit('SET_BUTTONS', {})
    commit('SET_PERMISSION_LOADED', false)
  }
}

/**
 * 格式化路由数据
 * 将后端返回的菜单数据转换为Vue Router可用的路由格式
 */
function formatRoutes(menuList) {
  const routes = []
  const routeNames = new Set() // 用于检测重复命名路由
  
  menuList.forEach(menu => {
    // 为非嵌套路由添加前导斜杠
    let path = menu.path
    if (path && !path.startsWith('/')) {
      path = `/${path}`
    }
    
    // 确保路由名称唯一性
    let name = menu.name
    if (routeNames.has(name)) {
      name = `${name}_${Date.now()}` // 添加时间戳确保唯一性
    }
    routeNames.add(name)
    
    const route = {
      path: path,
      name: name,
      meta: {
        title: menu.name,
        icon: menu.icon,
        id: menu.id
      }
    }
    
    // 设置组件路径 - 根据实际项目目录结构修正路径
    if (menu.component) {
      // 修复组件路径格式问题
      let componentPath = menu.component
      
      // 处理不同格式的组件路径
      if (!componentPath.startsWith('views/')) {
        // 如果路径以 .vue 结尾，去除 .vue 并添加正确的路径
        if (componentPath.endsWith('.vue')) {
          componentPath = componentPath.replace('.vue', '')
          
          // 如果路径不是以 / 开头，添加 views/
          if (!componentPath.startsWith('/')) {
            componentPath = `views/${componentPath}`
          } else {
            // 如果以 / 开头，去除 / 并添加 views/
            componentPath = `views${componentPath}`
          }
        } else {
          // 如果没有 .vue 后缀，根据path生成对应的组件路径
          const pathParts = path.split('/').filter(Boolean)
          let baseName = pathParts[pathParts.length - 1]
          
          // 特殊处理首页路径
          if (baseName === 'home') {
            componentPath = 'views/home/Home.vue'
          } else {
            // 对于其他路径，使用对应的文件夹和List.vue
            componentPath = `views/${baseName}/List.vue`
          }
        }
      }
      
      // 根据项目实际目录结构修正组件路径
      // 将模拟数据中的组件路径映射到实际文件位置
      const pathMapping = {
        'views/Users/List.vue': 'views/Users/List.vue',
        'views/Devices.vue': 'views/device-management/List.vue',
        'views/Clearance.vue': 'views/operation-management/Clearance.vue',
        'views/Partnership.vue': 'views/finance-cooperation/Partnership.vue',
        'views/Delivery.vue': 'views/operation-management/Delivery.vue',
        'views/WeightRecords.vue': 'views/data-records/WeightRecords.vue',
        'views/Finance.vue': 'views/finance-cooperation/Finance.vue',
        'views/Statistics.vue': 'views/statistics/Index.vue'
      }
      
      // 如果有映射关系，则使用映射后的路径
      if (pathMapping[componentPath]) {
        componentPath = pathMapping[componentPath]
      }
      
      // 动态导入组件
      route.component = () => import(`@/${componentPath}`)
    }
    
    // 处理子菜单
    if (menu.children && menu.children.length > 0) {
      route.children = formatRoutes(menu.children)
    }
    
    routes.push(route)
  })
  
  return routes
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}